import React from "react";
import {Box} from "@mui/material";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import type {NodeViewProps} from "@tiptap/react";
import {NodeViewWrapper} from "@tiptap/react";
import {variableChipSx} from "../../../common/variables/variableChipStyles";
import {getPrintoutVariableChipLabel} from "../../variables/printoutVariables";
import PrintoutBlockShell from "../shared/PrintoutBlockShell";
import DataListView from "./DataListView";
import {getDataListLayout} from "./dataListLayout";
import {getDataListAttrs} from "./printoutDataListOptions";
import type {DataListResolved, PrintoutDataListAttrs} from "./printoutDataListOptions";

const VariableChip = ({variableKey}: {variableKey: string}) => (
    <Box className="printout-variable-chip" component="span" sx={variableChipSx}>
        {getPrintoutVariableChipLabel(variableKey)}
    </Box>
);

const PrintoutDataListNodeView = (props: NodeViewProps) => {
    const editable = props.editor.isEditable;
    const attrs = getDataListAttrs(props.node.attrs as PrintoutDataListAttrs);
    // The editor uses chip rows; preview/print receives resolved values.
    const resolved = (props.node.attrs as {resolved?: DataListResolved | null}).resolved ?? null;
    const layout = getDataListLayout(attrs, resolved);

    // Keep unresolved blocks visible in the editor, but collapse empty resolved blocks for print.
    if (!editable && resolved && attrs.hideWhenEmpty && resolved.items.length === 0) {
        return <NodeViewWrapper style={{display: "none"}} data-type="printout-data-list-hidden"/>;
    }

    return (
        <PrintoutBlockShell
            {...props}
            blockType="printoutDataList"
            icon={<DatasetOutlinedIcon fontSize="small"/>}
            label="Data list"
        >
            <Box className="printout-data-list" sx={{width: "100%"}}>
                <DataListView
                    columns={layout.columns}
                    emptyText={attrs.emptyText}
                    renderCell={resolved
                        ? (column, rowIndex) => resolved.items[rowIndex]?.[column.id] ?? ""
                        : (column) => <VariableChip variableKey={column.variableKey}/>}
                    rowCount={resolved ? resolved.items.length : 3}
                    rowGroupLabels={layout.rowGroupLabels}
                    title={attrs.title}
                />
            </Box>
        </PrintoutBlockShell>
    );
};

export default PrintoutDataListNodeView;
